import React, { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

const loadTurnstile = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")));
      s.onerror = () => { scriptPromise = null; reject(new Error("turnstile script failed")); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

/**
 * Cloudflare Turnstile (managed mode). Calls onToken(token) when the visitor is
 * cleared and onToken(null) when the token expires or the widget errors, so the
 * parent can gate its submit button. Change `resetKey` to force a fresh challenge
 * (e.g. after the server rejected the previous token).
 */
const TurnstileWidget = ({ siteKey, onToken, resetKey = 0, action = "partner_signup" }) => {
  const hostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !hostRef.current) return;
        if (widgetIdRef.current !== null) {
          try { ts.remove(widgetIdRef.current); } catch (_) { /* already gone */ }
          widgetIdRef.current = null;
        }
        widgetIdRef.current = ts.render(hostRef.current, {
          sitekey: siteKey,
          theme: "dark",
          size: "flexible",
          action,
          callback: (token) => onTokenRef.current && onTokenRef.current(token || null),
          "expired-callback": () => onTokenRef.current && onTokenRef.current(null),
          "error-callback": () => onTokenRef.current && onTokenRef.current(null),
          "timeout-callback": () => onTokenRef.current && onTokenRef.current(null),
        });
      })
      .catch(() => { if (onTokenRef.current) onTokenRef.current(null); });
    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, resetKey, action]);

  if (!siteKey) return null;
  return <div ref={hostRef} style={{ minHeight: 65, margin: "0 0 12px" }} />;
};

export default TurnstileWidget;
